import { useEffect, useRef } from "react";
import { useStore, getEffectiveWeather } from "../store";

// Synthesized ambient soundscape (WebAudio, no external files).
export function useAmbient() {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const muted = useStore((s) => s.muted);
  const weather = useStore((s) => getEffectiveWeather(s));

  useEffect(() => {
    if (muted) {
      if (nodesRef.current) {
        nodesRef.current.master.gain.setTargetAtTime(0, ctxRef.current.currentTime, 0.4);
      }
      return;
    }
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      // pink-ish noise buffer
      const buf = ctx.createBuffer(1, ctx.sampleRate * 3, ctx.sampleRate);
      const data = buf.getChannelData(0);
      let last = 0;
      for (let i = 0; i < data.length; i++) {
        const white = Math.random() * 2 - 1;
        last = (last + 0.02 * white) / 1.02;
        data[i] = last * 3.5;
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      noise.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 600;
      const noiseGain = ctx.createGain();
      noiseGain.gain.value = 0.25;
      // low drone
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = 70;
      const oscGain = ctx.createGain();
      oscGain.gain.value = 0.05;
      const master = ctx.createGain();
      master.gain.value = 0;
      noise.connect(filter).connect(noiseGain).connect(master);
      osc.connect(oscGain).connect(master);
      master.connect(ctx.destination);
      noise.start();
      osc.start();
      ctxRef.current = ctx;
      nodesRef.current = { filter, noiseGain, master, osc };
    }
    const ctx = ctxRef.current;
    ctx.resume();
    nodesRef.current.master.gain.setTargetAtTime(0.6, ctx.currentTime, 0.6);
  }, [muted]);

  useEffect(() => {
    const n = nodesRef.current;
    if (!n || !ctxRef.current) return;
    const now = ctxRef.current.currentTime;
    if (weather === "rain") {
      n.filter.frequency.setTargetAtTime(2600, now, 0.5);
      n.noiseGain.gain.setTargetAtTime(0.55, now, 0.5);
    } else if (weather === "snow") {
      n.filter.frequency.setTargetAtTime(400, now, 0.5);
      n.noiseGain.gain.setTargetAtTime(0.12, now, 0.5);
    } else {
      n.filter.frequency.setTargetAtTime(700, now, 0.5);
      n.noiseGain.gain.setTargetAtTime(0.22, now, 0.5);
    }
  }, [weather]);
}
